// src/lib/services/recordingStorage.ts
import * as opfs from '@/lib/storage/opfs';

export type RecordingMode = 'screen_camera' | 'screen' | 'camera';

export interface RecordingMeta {
  recordingId: string;
  organizationId: string;
  userId: string;
  mode: RecordingMode;
  durationSeconds: number;
  createdAt: string;
  mimeType: string;
  videoSize: number;
  audioSize: number | null;
  thumbnailSize: number;
}

export interface PendingRecording {
  recordingId: string;
  meta: RecordingMeta;
  hasDbRow: boolean;
  dbStatus: string | null;
}

export interface PersistInput {
  video: Blob;
  audio: Blob | null;
  thumbnail: Blob;
  meta: Omit<RecordingMeta, 'videoSize' | 'audioSize' | 'thumbnailSize'>;
}

type DbStatusLookup = (recordingId: string) => Promise<{ status: string } | null>;

const RECORDINGS_ROOT = 'recordings';
const QUOTA_SAFETY_MULTIPLIER = 1.2; // 20% headroom over estimated size

function folderFor(recordingId: string): string {
  return `${RECORDINGS_ROOT}/${recordingId}`;
}

export async function canPersist(estimatedSize: number): Promise<boolean> {
  const cap = await opfs.detectStorageCapability();
  if (cap !== 'opfs') return false;
  const est = await opfs.estimateQuota();
  if (!est) return true;
  const free = est.quota - est.usage;
  return free > estimatedSize * QUOTA_SAFETY_MULTIPLIER;
}

export async function persist(input: PersistInput): Promise<void> {
  // INVARIANT: meta.json is written LAST. Its existence on disk is the atomicity
  // sentinel used by listPending() — if meta is missing, the recording is treated
  // as a partial write and the folder is cleaned up. Do not reorder.
  const { recordingId } = input.meta;
  const folder = folderFor(recordingId);
  console.log('[storage] persist start', {
    recordingId,
    videoSize: input.video.size,
    videoType: input.video.type,
    audioSize: input.audio?.size ?? null,
    thumbnailSize: input.thumbnail.size,
  });
  await opfs.writeBlob(`${folder}/video.webm`, input.video);
  if (input.audio) {
    await opfs.writeBlob(`${folder}/audio.webm`, input.audio);
  }
  await opfs.writeBlob(`${folder}/thumb.jpg`, input.thumbnail);
  const meta: RecordingMeta = {
    ...input.meta,
    videoSize: input.video.size,
    audioSize: input.audio?.size ?? null,
    thumbnailSize: input.thumbnail.size,
  };
  await opfs.writeJSON(`${folder}/meta.json`, meta);
}

export async function readVideo(recordingId: string): Promise<Blob | null> {
  return opfs.readBlob(`${folderFor(recordingId)}/video.webm`);
}

export async function readAudio(recordingId: string): Promise<Blob | null> {
  return opfs.readBlob(`${folderFor(recordingId)}/audio.webm`);
}

export async function readThumbnail(recordingId: string): Promise<Blob | null> {
  return opfs.readBlob(`${folderFor(recordingId)}/thumb.jpg`);
}

export async function readMeta(recordingId: string): Promise<RecordingMeta | null> {
  return opfs.readJSON<RecordingMeta>(`${folderFor(recordingId)}/meta.json`);
}

export async function deleteRecording(recordingId: string): Promise<void> {
  await opfs.deleteFolder(folderFor(recordingId));
}

export async function listPending(
  dbLookup: DbStatusLookup
): Promise<PendingRecording[]> {
  const folders = await opfs.listFolders(RECORDINGS_ROOT);
  const pending: PendingRecording[] = [];
  for (const recordingId of folders) {
    const meta = await readMeta(recordingId);
    if (!meta) {
      await deleteRecording(recordingId);
      continue;
    }
    const row = await dbLookup(recordingId);
    if (row?.status === 'ready') {
      await deleteRecording(recordingId);
      continue;
    }
    pending.push({
      recordingId,
      meta,
      hasDbRow: !!row,
      dbStatus: row?.status ?? null,
    });
  }
  return pending;
}

export async function purgeOldPending(maxAgeMs: number = 30 * 24 * 60 * 60 * 1000): Promise<number> {
  const folders = await opfs.listFolders(RECORDINGS_ROOT);
  const now = Date.now();
  let purged = 0;
  for (const recordingId of folders) {
    const meta = await readMeta(recordingId);
    if (!meta) {
      await deleteRecording(recordingId);
      purged++;
      continue;
    }
    const age = now - new Date(meta.createdAt).getTime();
    if (age > maxAgeMs) {
      await deleteRecording(recordingId);
      purged++;
    }
  }
  return purged;
}
